import { CodeSnippet } from "@/components/code-snippet"
import { useAppStore } from "@/store"
import { extractOpenGraphData } from "@/utils/extract-opengraph"
import { Badge } from "@preview-kit/ui/components/badge"
import { Button } from "@preview-kit/ui/components/button"
import { Separator } from "@preview-kit/ui/components/separator"
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@preview-kit/ui/components/tabs"
import { cn } from "@preview-kit/ui/lib/utils"
import { ArrowLeft, CircleAlert } from "lucide-react"
import { useEffect, useState } from "react"
import { useShallow } from "zustand/shallow"
import CommandListView from "../command-list"

export default function OpenGraphView() {
	const { setNavigation, setAnimateClick } = useAppStore(
		useShallow((state) => ({
			setNavigation: state.setNavigation,
			setAnimateClick: state.setAnimateClick,
		})),
	)

	const [ogData, setOgData] = useState(() => extractOpenGraphData())

	// Update Open Graph data when component mounts or when page changes
	useEffect(() => {
		const updateOgData = () => {
			setOgData(extractOpenGraphData())
		}

		// Listen for navigation changes (for SPAs)
		window.addEventListener("popstate", updateOgData)

		// Also check for mutations in the head element (in case meta tags are updated dynamically)
		const observer = new MutationObserver(() => {
			// Debounce the updates to avoid too frequent refreshes
			setTimeout(updateOgData, 100)
		})
		observer.observe(document.head, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ["content", "property", "name"],
		})

		return () => {
			window.removeEventListener("popstate", updateOgData)
			observer.disconnect()
		}
	}, [])

	return (
		<>
			<div className={cn("flex-1 overflow-y-auto ", "scroll_area")}>
				<Button
					size="icon"
					className="absolute top-4 left-4 z-50 w-7 h-7 flex items-center justify-center bg-neutral-200"
					variant="outline"
					onClick={() => {
						setAnimateClick()
						setNavigation({
							currentView: <CommandListView />,
							breadcrumb: [],
							viewStack: [],
						})
					}}
				>
					<ArrowLeft />
				</Button>
				<div>
					<div className="sticky top-0 pt-4 bg-white z-40">
						<div className="flex items-center justify-between px-4 ml-10">
							<h1 className="text-xl font-medium text-gray-900">
								Open Graph Preview
							</h1>
						</div>
						<Separator className="my-4" />
					</div>

					<div className="px-4">
						<Tabs
							defaultValue="linkedin"
							className="w-full flex items-center justify-center"
						>
							<TabsList className="mb-4">
								<TabsTrigger value="linkedin">LinkedIn</TabsTrigger>
								<TabsTrigger value="slack">Slack</TabsTrigger>
								<TabsTrigger value="discord">Discord</TabsTrigger>
								<TabsTrigger value="twitter">Twitter</TabsTrigger>
							</TabsList>
							<TabsContent value="linkedin" className="w-full">
								<div className="border rounded-lg overflow-hidden shadow">
									{ogData.ogImage ? (
										<img
											src={ogData.ogImage}
											alt={
												ogData.ogImageAlt ||
												ogData.ogTitle ||
												"Open Graph preview"
											}
											width={504}
											height={350}
											className="w-full h-auto"
										/>
									) : (
										<div className="w-full h-[350px] bg-gray-100 flex items-center justify-center text-gray-500">
											No Open Graph image found
										</div>
									)}
								</div>
							</TabsContent>
							<TabsContent value="slack" className="w-full">
								{ogData.ogImage ? (
									<img
										src={ogData.ogImage}
										alt={
											ogData.ogImageAlt ||
											ogData.ogTitle ||
											"Open Graph preview"
										}
										width={504}
										height={350}
										className="w-full h-auto"
									/>
								) : (
									<div className="w-full h-[350px] bg-gray-100 flex items-center justify-center text-gray-500">
										No Open Graph image found
									</div>
								)}
							</TabsContent>
							<TabsContent value="discord" className="w-full">
								<div className="border rounded-lg overflow-hidden shadow">
									{ogData.ogImage ? (
										<img
											src={ogData.ogImage}
											alt={
												ogData.ogImageAlt ||
												ogData.ogTitle ||
												"Open Graph preview"
											}
											width={504}
											height={350}
											className="w-full h-auto"
										/>
									) : (
										<div className="w-full h-[350px] bg-gray-100 flex items-center justify-center text-gray-500">
											No Open Graph image found
										</div>
									)}
								</div>
							</TabsContent>
							<TabsContent value="twitter" className="w-full">
								{ogData.ogImage || ogData.twitterImage ? (
									<img
										src={ogData.twitterImage || ogData.ogImage}
										alt={
											ogData.twitterImageAlt ||
											ogData.ogImageAlt ||
											ogData.twitterTitle ||
											ogData.ogTitle ||
											"Twitter card preview"
										}
										width={504}
										height={350}
										className="w-full h-auto"
									/>
								) : (
									<div className="w-full h-[350px] bg-gray-100 flex items-center justify-center text-gray-500">
										No Twitter image found
									</div>
								)}
							</TabsContent>
						</Tabs>
					</div>

					<div className="space-y-6 mt-3">
						<div className="px-2 text-black">
							<table className="w-full border-collapse table-fixed">
								<tbody>
									<tr className="border-b">
										<td className="py-3 px-3">
											<div className="flex items-center justify-between mb-2">
												<div className="font-mono text-sm text-gray-700 flex items-center gap-1">
													{!ogData.ogType && (
														<CircleAlert className="w-4 h-4 text-orange-500" />
													)}
													og:type
												</div>
												<div className="text-sm">
													{ogData.ogType || (
														<Badge variant="destructive">Missing</Badge>
													)}
												</div>
											</div>
											{!ogData.ogType && (
												<div className="mt-2">
													<CodeSnippet
														code={`<meta property="og:type" content="website" />`}
														language="html"
													/>
												</div>
											)}
										</td>
									</tr>
									<tr className="border-b">
										<td className="py-3 px-3">
											<div className="flex items-center justify-between mb-2">
												<div className="font-mono text-sm text-gray-700 flex items-center gap-1">
													{!ogData.ogTitle && (
														<CircleAlert className="w-4 h-4 text-orange-500" />
													)}
													og:title
												</div>
												<div className="text-sm">
													{ogData.ogTitle || (
														<Badge variant="destructive">Missing</Badge>
													)}
												</div>
											</div>
											{!ogData.ogTitle && (
												<div className="mt-2">
													<CodeSnippet
														code={`<meta property="og:title" content="Your page title" />`}
														language="html"
													/>
												</div>
											)}
										</td>
									</tr>
									<tr className="border-b">
										<td className="py-3 px-3">
											<div className="flex items-center justify-between mb-2">
												<div className="font-mono text-sm text-gray-700 flex items-center gap-1">
													{!ogData.ogDescription && (
														<CircleAlert className="w-4 h-4 text-orange-500" />
													)}
													og:description
												</div>
												<div className="text-sm">
													{ogData.ogDescription || (
														<Badge variant="destructive">Missing</Badge>
													)}
												</div>
											</div>
											{!ogData.ogDescription && (
												<div className="mt-2">
													<CodeSnippet
														code={`<meta property="og:description" content="Your page description" />`}
														language="html"
													/>
												</div>
											)}
										</td>
									</tr>
									<tr className="border-b">
										<td className="py-3 px-3">
											<div className="flex items-center justify-between mb-2">
												<div className="font-mono text-sm text-gray-700 flex items-center gap-1">
													{!ogData.ogUrl && (
														<CircleAlert className="w-4 h-4 text-orange-500" />
													)}
													og:url
												</div>
												<div className="text-sm">
													{ogData.ogUrl || (
														<Badge variant="destructive">Missing</Badge>
													)}
												</div>
											</div>
											{!ogData.ogUrl && (
												<div className="mt-2">
													<CodeSnippet
														code={`<meta property="og:url" content="${typeof window !== "undefined" ? window.location.href : "https://yoursite.com"}" />`}
														language="html"
													/>
												</div>
											)}
										</td>
									</tr>
									<tr className="border-b">
										<td className="py-3 px-3">
											<div className="flex items-center justify-between mb-2">
												<div className="font-mono text-sm text-gray-700 flex items-center gap-1">
													{!ogData.ogImage && (
														<CircleAlert className="w-4 h-4 text-orange-500" />
													)}
													og:image
												</div>
												<div className="text-sm">
													{ogData.ogImage || (
														<Badge variant="destructive">Missing</Badge>
													)}
												</div>
											</div>
											{!ogData.ogImage && (
												<div className="mt-2">
													<CodeSnippet
														code={`<meta property="og:image" content="https://yoursite.com/og-image.jpg" />`}
														language="html"
													/>
												</div>
											)}
										</td>
									</tr>
									<tr className="border-b">
										<td className="py-3 px-3">
											<div className="flex items-center justify-between mb-2">
												<div className="font-mono text-sm text-gray-700 flex items-center gap-1">
													{!ogData.ogLocale && (
														<CircleAlert className="w-4 h-4 text-orange-500" />
													)}
													og:locale
												</div>
												<div className="text-sm">
													{ogData.ogLocale || (
														<Badge variant="destructive">Missing</Badge>
													)}
												</div>
											</div>
											{!ogData.ogLocale && (
												<div className="mt-2">
													<CodeSnippet
														code={`<meta property="og:locale" content="en_US" />`}
														language="html"
													/>
												</div>
											)}
										</td>
									</tr>
									<tr className="border-b">
										<td className="py-3 px-3">
											<div className="flex items-center justify-between mb-2">
												<div className="font-mono text-sm text-gray-700 flex items-center gap-1">
													{!ogData.ogImageAlt && (
														<CircleAlert className="w-4 h-4 text-orange-500" />
													)}
													og:image:alt
												</div>
												<div className="text-sm">
													{ogData.ogImageAlt || (
														<Badge variant="destructive">Missing</Badge>
													)}
												</div>
											</div>
											{!ogData.ogImageAlt && (
												<div className="mt-2">
													<CodeSnippet
														code={`<meta property="og:image:alt" content="Description of your image" />`}
														language="html"
													/>
												</div>
											)}
										</td>
									</tr>
									<tr className="border-b">
										<td className="py-3 px-3">
											<div className="flex items-center justify-between mb-2">
												<div className="font-mono text-sm text-gray-700 flex items-center gap-1">
													{!ogData.ogImageWidth && (
														<CircleAlert className="w-4 h-4 text-orange-500" />
													)}
													og:image:width
												</div>
												<div className="text-sm">
													{ogData.ogImageWidth || (
														<Badge variant="destructive">Missing</Badge>
													)}
												</div>
											</div>
											{!ogData.ogImageWidth && (
												<div className="mt-2">
													<CodeSnippet
														code={`<meta property="og:image:width" content="1200" />`}
														language="html"
													/>
												</div>
											)}
										</td>
									</tr>
									<tr className="border-b">
										<td className="py-3 px-3">
											<div className="flex items-center justify-between mb-2">
												<div className="font-mono text-sm text-gray-700 flex items-center gap-1">
													{!ogData.ogImageHeight && (
														<CircleAlert className="w-4 h-4 text-orange-500" />
													)}
													og:image:height
												</div>
												<div className="text-sm">
													{ogData.ogImageHeight || (
														<Badge variant="destructive">Missing</Badge>
													)}
												</div>
											</div>
											{!ogData.ogImageHeight && (
												<div className="mt-2">
													<CodeSnippet
														code={`<meta property="og:image:height" content="630" />`}
														language="html"
													/>
												</div>
											)}
										</td>
									</tr>
									<tr className="border-b">
										<td className="py-3 px-3">
											<div className="flex items-center justify-between mb-2">
												<div className="font-mono text-sm text-gray-700 flex items-center gap-1">
													{!ogData.twitterCard && (
														<CircleAlert className="w-4 h-4 text-orange-500" />
													)}
													twitter:card
												</div>
												<div className="text-sm">
													{ogData.twitterCard || (
														<Badge variant="destructive">Missing</Badge>
													)}
												</div>
											</div>
											{!ogData.twitterCard && (
												<div className="mt-2">
													<CodeSnippet
														code={`<meta name="twitter:card" content="summary_large_image" />`}
														language="html"
													/>
												</div>
											)}
										</td>
									</tr>
									<tr className="border-b">
										<td className="py-3 px-3">
											<div className="flex items-center justify-between mb-2">
												<div className="font-mono text-sm text-gray-700 flex items-center gap-1">
													{!ogData.twitterTitle && (
														<CircleAlert className="w-4 h-4 text-orange-500" />
													)}
													twitter:title
												</div>
												<div className="text-sm">
													{ogData.twitterTitle || (
														<Badge variant="destructive">Missing</Badge>
													)}
												</div>
											</div>
											{!ogData.twitterTitle && (
												<div className="mt-2">
													<CodeSnippet
														code={`<meta name="twitter:title" content="Your page title" />`}
														language="html"
													/>
												</div>
											)}
										</td>
									</tr>
									<tr className="border-b">
										<td className="py-3 px-3">
											<div className="flex items-center justify-between mb-2">
												<div className="font-mono text-sm text-gray-700 flex items-center gap-1">
													{!ogData.twitterDescription && (
														<CircleAlert className="w-4 h-4 text-orange-500" />
													)}
													twitter:description
												</div>
												<div className="text-sm">
													{ogData.twitterDescription || (
														<Badge variant="destructive">Missing</Badge>
													)}
												</div>
											</div>
											{!ogData.twitterDescription && (
												<div className="mt-2">
													<CodeSnippet
														code={`<meta name="twitter:description" content="Your page description" />`}
														language="html"
													/>
												</div>
											)}
										</td>
									</tr>
									<tr className="border-b">
										<td className="py-3 px-3">
											<div className="flex items-center justify-between mb-2">
												<div className="font-mono text-sm text-gray-700 flex items-center gap-1">
													{!ogData.twitterImage && (
														<CircleAlert className="w-4 h-4 text-orange-500" />
													)}
													twitter:image
												</div>
												<div className="text-sm">
													{ogData.twitterImage || (
														<Badge variant="destructive">Missing</Badge>
													)}
												</div>
											</div>
											{!ogData.twitterImage && (
												<div className="mt-2">
													<CodeSnippet
														code={`<meta name="twitter:image" content="https://yoursite.com/twitter-image.jpg" />`}
														language="html"
													/>
												</div>
											)}
										</td>
									</tr>
									<tr className="border-b">
										<td className="py-3 px-3">
											<div className="flex items-center justify-between mb-2">
												<div className="font-mono text-sm text-gray-700 flex items-center gap-1">
													{!ogData.twitterImageAlt && (
														<CircleAlert className="w-4 h-4 text-orange-500" />
													)}
													twitter:image:alt
												</div>
												<div className="text-sm">
													{ogData.twitterImageAlt || (
														<Badge variant="destructive">Missing</Badge>
													)}
												</div>
											</div>
											{!ogData.twitterImageAlt && (
												<div className="mt-2">
													<CodeSnippet
														code={`<meta name="twitter:image:alt" content="Description of your Twitter image" />`}
														language="html"
													/>
												</div>
											)}
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</>
	)
}
